import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  BarChart3,
  Users,
  FileText,
  Settings,
  Activity,
  ChevronLeft,
  ChevronRight,
  LogOut,
  KeyRound,
  Receipt,
  Monitor,
  Terminal,
  Megaphone,
  Headset,
  ShieldCheck,
  Flag,
  UserSearch,
  MousePointerClick,
  ClipboardList,
  MessageSquare,
  GanttChart,
} from 'lucide-react';
import { useState } from 'react';
import useAuthStore, { PERMISSIONS } from '../../stores/authStore';
import useThemeStore from '../../stores/themeStore';
import nolagLogoWhite from '../../assets/nolag-logo-white.svg';
import nolagLogoBlack from '../../assets/nolag-logo-black.svg';

// Navigation is grouped into sections. Each item may declare a `permission`
// (superadmin bypasses all). `scaffold` items are visible placeholders whose
// backend exists but isn't wired yet; `planned` items have no backend yet.
const navSections = [
  {
    title: 'Main',
    items: [
      { label: 'Overview', icon: LayoutDashboard, path: '/dashboard', description: 'Executive dashboard' },
      { label: 'Analytics', icon: BarChart3, path: '/analytics', permission: PERMISSIONS.VIEW_FINANCIAL_ANALYTICS, description: 'Business intelligence' },
      { label: 'MIS', icon: ClipboardList, path: '/mis', description: 'KPIs by day, week & month' },
      { label: 'Team progress', icon: GanttChart, path: '/team-progress', description: 'Product work from Jira' },
      { label: 'Activity tracker', icon: MousePointerClick, path: '/admin-activity', permission: PERMISSIONS.VIEW_ADMIN_ACTIVITY, description: 'Admin dashboard usage' },
      { label: 'System Health', icon: Activity, path: '/system-health', permission: PERMISSIONS.VIEW_PC_HEALTH, description: 'Infrastructure status' },
    ],
  },
  {
    title: 'Cafés & Licensing',
    items: [
      { label: 'Cafés', icon: Building2, path: '/cafes', permission: PERMISSIONS.VIEW_CAFE_REGISTRY, description: 'Tenant registry & onboarding' },
      // Subscriptions removed from nav (2026-08-21, user request) -- the page
      // shows fabricated MRR (max_pcs * 500) over fake plan tiers with no real
      // billing behind it; Billing & Revenue's licensing table (fed by the
      // same /internal/cafes license_status the server actually enforces) is
      // the real commercial state. Still routed in App.jsx, just not linked.
      { label: 'Licenses', icon: KeyRound, path: '/licenses', permission: PERMISSIONS.VIEW_SUBSCRIPTIONS, description: 'Issue & track keys' },
      { label: 'Billing & Revenue', icon: Receipt, path: '/billing', permission: PERMISSIONS.VIEW_SUBSCRIPTIONS, description: 'Invoices & reports', scaffold: true },
    ],
  },
  {
    title: 'Fleet',
    items: [
      { label: 'PC Registry', icon: Monitor, path: '/pcs', permission: PERMISSIONS.VIEW_PC_HEALTH, description: 'All kiosk PCs', scaffold: true },
      // Remote Commands removed from nav (2026-07-09, user request) -- PC
      // command dispatch (lock/unlock/console/etc) now lives directly on
      // each café's detail page (CafeDetail.jsx "Registered PCs" section),
      // so this separate page duplicated the same functionality. Still
      // routed in App.jsx, just not linked from here.
      // { label: 'Remote Commands', icon: Terminal, path: '/commands', permission: PERMISSIONS.EXECUTE_PC_COMMANDS, description: 'Dispatch to kiosks' },
    ],
  },
  {
    title: 'Communications',
    items: [
      { label: 'Announcements', icon: Megaphone, path: '/announcements', description: 'Broadcast banners', scaffold: true },
      { label: 'Support Tickets', icon: Headset, path: '/support', description: 'Cross-tenant queue', scaffold: true },
      { label: 'Customer Feedback', icon: MessageSquare, path: '/customer-feedback', description: 'From kiosk logouts' },
    ],
  },
  {
    title: 'Administration',
    items: [
      { label: 'Users', icon: Users, path: '/users', permission: PERMISSIONS.VIEW_USERS, description: 'Platform team' },
      // Roles & Permissions hidden for now (2026-07-06, user request) — a
      // full implementation plan is documented at the top of
      // pages/RolesPermissions.jsx (still routed in App.jsx, just not
      // linked from here). Re-add this entry when it's prioritized again:
      // { label: 'Roles & Permissions', icon: ShieldCheck, path: '/roles', permission: PERMISSIONS.MANAGE_PERMISSIONS, description: 'Capability matrix' },
      { label: 'Audit Logs', icon: FileText, path: '/audit-logs', permission: PERMISSIONS.VIEW_AUDIT_LOGS, description: 'Activity history' },
      { label: 'Settings', icon: Settings, path: '/settings', description: 'System configuration' },
    ],
  },
  {
    title: 'Planned',
    items: [
      // Feature Flags hidden for now (2026-07-08, user request) — no backend
      // yet (see description below), still routed in App.jsx, just not
      // linked from here. Re-add when a backend exists:
      // { label: 'Feature Flags', icon: Flag, path: '/feature-flags', description: 'No backend yet', planned: true },
      { label: 'Cross-Tenant Search', icon: UserSearch, path: '/cross-tenant-search', description: 'Find a customer across cafés' },
    ],
  },
];

export default function Sidebar() {
  const location = useLocation();
  const { user, hasPermission, logout, isSuperAdmin } = useAuthStore();
  const { theme } = useThemeStore();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const logoSrc = theme === 'light' ? nolagLogoBlack : nolagLogoWhite;

  const renderNavItem = (item) => {
    if (item.permission && !hasPermission(item.permission) && !isSuperAdmin()) {
      return null;
    }

    const Icon = item.icon;
    const isActive = location.pathname === item.path ||
      (item.path !== '/dashboard' && location.pathname.startsWith(item.path));

    return (
      <NavLink
        key={item.path}
        to={item.path}
        className={`sidebar__nav-item ${isActive ? 'sidebar__nav-item--active' : ''}`}
        title={isCollapsed ? item.label : undefined}
      >
        <div className="sidebar__nav-icon">
          <Icon size={20} />
        </div>
        {!isCollapsed && (
          <div className="sidebar__nav-content">
            <span className="sidebar__nav-label">
              {item.label}
              {item.scaffold && <span className="sidebar__tag sidebar__tag--scaffold" title="Backend ready — UI scaffold">•</span>}
              {item.planned && <span className="sidebar__tag sidebar__tag--planned" title="Planned — no backend yet">•</span>}
            </span>
            <span className="sidebar__nav-desc">{item.description}</span>
          </div>
        )}
        {isActive && <div className="sidebar__nav-indicator" />}
      </NavLink>
    );
  };

  return (
    <aside className={`sidebar ${isCollapsed ? 'sidebar--collapsed' : ''}`}>
      {/* Logo */}
      <div className="sidebar__header">
        <div className="sidebar__logo">
          <img src={logoSrc} alt="NOLAG" className="sidebar__logo-img" />
        </div>
        <button
          className="sidebar__collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Primary Navigation */}
      <nav className="sidebar__nav">
        {navSections.map((section) => {
          const items = section.items
            .map(renderNavItem)
            .filter(Boolean);
          if (items.length === 0) return null;
          return (
            <div className="sidebar__nav-section" key={section.title}>
              {!isCollapsed && <span className="sidebar__nav-title">{section.title}</span>}
              <div className="sidebar__nav-list">{items}</div>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="sidebar__footer">
        {/* User Section */}
        <div className="sidebar__user">
          <div className="sidebar__user-avatar">
            {user?.username?.charAt(0).toUpperCase() || 'A'}
          </div>
          {!isCollapsed && (
            <div className="sidebar__user-info">
              <span className="sidebar__user-name">{user?.username || 'Admin'}</span>
              <span className="sidebar__user-role">
                {user?.role === 'superadmin' ? 'Super Admin' : user?.role}
              </span>
            </div>
          )}
          <button
            className="sidebar__logout-btn"
            onClick={logout}
            title="Sign out"
          >
            <LogOut size={18} />
          </button>
        </div>
      </div>

      <style>{`
        .sidebar {
          /* noLag rebrand: scoped to the sidebar only — rest of the app
             still reads the blue --accent-primary from index.css until
             its pages get the same treatment. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);

          /* noLag rebrand: Figma's sidebar floats inset from the viewport
             edges as a rounded panel, not a flush edge-to-edge rail.
             Background is the exact recipe from the Figma node
             (get_design_context on 2467:31): pure backdrop-blur, no base
             fill, plus a WHITE gradient sheen — same material as the stat
             cards, see Dashboard.jsx. Mixing var(--glass-bg) in as a base
             (an earlier pass) added a gray wash that desaturated the
             canvas color instead of letting it show straight through.
             Radius is Figma's literal 30px, not --radius-2xl (24px). */
          position: fixed;
          left: var(--shell-gap);
          top: var(--shell-gap);
          bottom: var(--shell-gap);
          width: var(--sidebar-width);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0.07) 45%, rgba(255, 255, 255, 0.04) 100%);
          backdrop-filter: blur(14px) saturate(180%);
          -webkit-backdrop-filter: blur(14px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.18);
          border-radius: 30px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          z-index: 100;
          transition: width var(--duration-base) var(--ease-out);
        }

        .sidebar::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--glass-noise);
          opacity: 0.008;
          pointer-events: none;
        }

        .sidebar--collapsed {
          width: var(--sidebar-collapsed);
        }

        /* Header — fixed to match the top bar's --header-height so their
           bottom borders line up instead of stepping at the corner. */
        .sidebar__header {
          height: var(--header-height);
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 var(--space-5);
          border-bottom: 1px solid var(--divider);
        }

        .sidebar--collapsed .sidebar__header {
          height: auto;
          flex-direction: column;
          justify-content: center;
          gap: var(--space-3);
          padding: var(--space-4) var(--space-2);
        }

        .sidebar__logo {
          display: flex;
          align-items: center;
          gap: var(--space-3);
        }

        .sidebar__logo-img {
          height: 44px;
          width: auto;
          max-width: 100%;
          display: block;
        }

        .sidebar--collapsed .sidebar__logo-img {
          height: 28px;
        }

        .sidebar__collapse-btn {
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: 1px solid var(--border-default);
          border-radius: var(--radius-sm);
          color: var(--text-tertiary);
          cursor: pointer;
          transition: all var(--duration-fast) var(--ease-out);
        }

        .sidebar__collapse-btn:hover {
          background: var(--glass-bg-hover);
          border-color: var(--border-hover);
          color: var(--text-primary);
        }

        /* Navigation */
        .sidebar__nav {
          flex: 1;
          padding: var(--space-4);
          overflow-y: auto;
          overflow-x: hidden;
        }

        .sidebar__nav-section {
          margin-bottom: var(--space-6);
        }

        .sidebar__nav-section:last-child {
          margin-bottom: 0;
        }

        /* Collapsed mode hides the section title (just an icon rail), so
           the same 24px gap meant to separate a title from the next
           section's icons is now dead space between two icon groups with
           nothing to explain it — with ~13 nav items total, that alone was
           enough to force scrolling that wasn't otherwise necessary. */
        .sidebar--collapsed .sidebar__nav-section {
          margin-bottom: var(--space-2);
        }

        .sidebar__nav-title {
          display: block;
          font-size: var(--text-xs);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--text-quaternary);
          padding: var(--space-2) var(--space-3);
          margin-bottom: var(--space-2);
        }

        .sidebar__nav-list {
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
        }

        .sidebar__nav-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-3);
          border-radius: var(--radius-md);
          color: var(--text-secondary);
          transition: all var(--duration-fast) var(--ease-out);
          overflow: hidden;
        }

        /* Collapsed mode renders only the icon (.sidebar__nav-content is
           omitted entirely) — the item still stretches to the rail's full
           40px content width (72px sidebar − 16px nav padding × 2), but
           12px padding each side left only 16px for a 24px icon, so it
           overflowed its own padding box and sat skewed toward the left
           edge (flex's default justify-content: flex-start eats the
           deficit from the trailing side only). Drop horizontal padding
           and center instead, so the icon sits centered in the full
           available width with no overflow. */
        .sidebar--collapsed .sidebar__nav-item {
          justify-content: center;
          padding: var(--space-3) 0;
        }

        .sidebar__nav-item:hover {
          background: rgba(255, 255, 255, 0.04);
          color: var(--text-primary);
        }

        /* Figma keeps the active row's icon/text white/neutral — orange is
           only the left indicator bar below, not a text/background tint.
           Matching that instead of the earlier all-orange treatment. */
        .sidebar__nav-item--active {
          background: #262626;
          color: var(--text-primary);
        }

        .sidebar__nav-item--active .sidebar__nav-label {
          font-weight: 600;
        }

        .sidebar__nav-item--active:hover {
          background: #262626;
        }

        .sidebar__nav-icon {
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .sidebar__nav-content {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .sidebar__nav-label {
          font-size: var(--text-sm);
          font-weight: 500;
        }

        .sidebar__tag {
          margin-left: 6px;
          font-size: 14px;
          line-height: 1;
          vertical-align: middle;
        }
        .sidebar__tag--scaffold { color: var(--accent-primary); }
        .sidebar__tag--planned { color: var(--status-warning); }

        .sidebar__nav-desc {
          font-size: var(--text-xs);
          color: var(--text-tertiary);
          opacity: 0;
          transition: opacity var(--duration-fast) var(--ease-out);
        }

        .sidebar__nav-item:hover .sidebar__nav-desc {
          opacity: 1;
        }

        .sidebar__nav-item--active .sidebar__nav-desc {
          color: var(--text-tertiary);
          opacity: 0.7;
        }

        .sidebar__nav-indicator {
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 3px;
          height: 20px;
          background: var(--accent-primary);
          border-radius: 0 2px 2px 0;
        }

        /* Footer */
        .sidebar__footer {
          padding: var(--space-4);
          border-top: 1px solid var(--divider);
        }

        .sidebar__user {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          padding: var(--space-3);
          background: rgba(0, 0, 0, 0.2);
          border-radius: var(--radius-md);
        }

        .sidebar__user-avatar {
          width: 36px;
          height: 36px;
          background: var(--accent-gradient);
          border-radius: var(--radius-md);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: var(--text-sm);
          color: white;
          flex-shrink: 0;
        }

        .sidebar__user-info {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
        }

        .sidebar__user-name {
          font-size: var(--text-sm);
          font-weight: 500;
          color: var(--text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .sidebar__user-role {
          font-size: var(--text-xs);
          color: var(--text-tertiary);
        }

        .sidebar__logout-btn {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: transparent;
          border: none;
          color: var(--text-tertiary);
          cursor: pointer;
          border-radius: var(--radius-sm);
          transition: all var(--duration-fast) var(--ease-out);
        }

        .sidebar__logout-btn:hover {
          background: var(--status-danger-subtle);
          color: var(--status-danger);
        }

        .sidebar--collapsed .sidebar__user-info {
          display: none;
        }

        .sidebar--collapsed .sidebar__user {
          padding: var(--space-2);
          justify-content: center;
        }

        .sidebar--collapsed .sidebar__logout-btn {
          display: none;
        }
      `}</style>
    </aside>
  );
}
